import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLazyGetAllPermissionsQuery } from '../store/api';

const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const [fetchAllPermissions] = useLazyGetAllPermissionsQuery();
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchPermissions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      setLoading(true);
      const response = await fetchAllPermissions().unwrap();
      const permissionsData = Array.isArray(response)
        ? response
        : (response?.data ?? response ?? []);
      const permissions = permissionsData.map((perm) => ({
        id: perm.id,
        name: perm.name,
        key: perm.name?.toLowerCase().replace(/\s+/g, '-') || perm.id,
      }));
      setAllPermissions(permissions);
    } catch (err) {
      if (err?.status === 403 || err?.response?.status === 403) {
        setAllPermissions([]);
      } else {
        console.error('Failed to load permissions:', err);
        setAllPermissions([]);
      }
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [fetchAllPermissions]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !hasFetched) {
      fetchPermissions();
    }
  }, [hasFetched, fetchPermissions]);

  const refetch = useCallback(() => {
    setHasFetched(false);
  }, []);

  const value = useMemo(
    () => ({
      allPermissions,
      loading,
      refetch,
      fetchPermissions,
    }),
    [allPermissions, loading, refetch, fetchPermissions]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};
